import { createStore } from '@stencil/store';
import { User } from '@firebase/auth';
import { QueryDocumentSnapshot, collection, doc, query, where } from '@firebase/firestore';
import { authState } from 'rxfire/auth';
import { collection as collectionSnapshots, collectionData, docData } from 'rxfire/firestore';
import { EMPTY, Subscription, catchError, map, skipWhile, takeWhile } from 'rxjs';

import { FirebaseService, orderConverter } from '../utils/firebase';
import { selection } from './selection.store';
import { settings } from './settings.store';

import { BusinessCustomer, PlatformCustomer } from '@artask/tambr-models/models/customer.model';
import { Order } from '@artask/tambr-models/models/orders.model';


interface State {
  user: User;
  userSubscription: Subscription;

  businessCustomer: BusinessCustomer;
  businessCustomerLoading: boolean;
  businessCustomerLoaded: boolean;
  businessCustomerSubscription: Subscription;

  platformCustomer: PlatformCustomer;
  platformCustomerLoading: boolean;
  platformCustomerLoaded: boolean;
  platformCustomerSubscription: Subscription;

  signUpProcessLoading: boolean;

  customerOrders: Order[];
  customerOrdersLoading: boolean;
  customerOrdersLoaded: boolean;
  customerOrdersSubscription: Subscription;
}

const { state, onChange } = createStore<State>({
  user: null,
  userSubscription: null,

  businessCustomer: null,
  businessCustomerLoading: false,
  businessCustomerLoaded: false,
  businessCustomerSubscription: null,

  platformCustomer: null,
  platformCustomerLoading: false,
  platformCustomerLoaded: false,
  platformCustomerSubscription: null,

  signUpProcessLoading: false,

  customerOrders: [],
  customerOrdersLoading: false,
  customerOrdersLoaded: false,
  customerOrdersSubscription: null,
});


onChange( 'platformCustomer', (platformCustomer: PlatformCustomer) => {
  if ( state.signUpProcessLoading && platformCustomer && state.businessCustomer ) {
    state.signUpProcessLoading = false;
  }
});

onChange( 'businessCustomer', (businessCustomer: BusinessCustomer) => {
  if ( state.signUpProcessLoading && businessCustomer && state.platformCustomer ) {
    state.signUpProcessLoading = false;
  }
});

onChange( 'businessCustomerLoading', (loading: boolean) => console.log('businessCustomerLoading', loading) );
onChange( 'businessCustomerLoaded', (loaded: boolean) => console.log('businessCustomerLoaded', loaded) );
onChange( 'platformCustomerLoading', (loading: boolean) => console.log('platformCustomerLoading', loading) );
onChange( 'platformCustomerLoaded', (loaded: boolean) => console.log('platformCustomerLoaded', loaded) );
onChange( 'signUpProcessLoading', (loading: boolean) => console.log('signUpProcessLoading', loading) );


function setUserListener() {
  state.userSubscription = authState( FirebaseService.auth ).subscribe( user => {
    console.log( 'user', user );
    if ( user ) {
      state.user = user;
      setPlatformCustomerListener();
      setBusinessCustomerListener();
      setCustomerOrdersListener();
    } else {
      state.user = null;
      state.platformCustomer = null;
      state.businessCustomer = null;
      state.customerOrders = null;
    }
  });
}


function setPlatformCustomerListener() {
  if ( !state.user ) return;
  state.platformCustomerLoading = true;

  if ( state.platformCustomerSubscription ) state.platformCustomerSubscription.unsubscribe();
  return docData( 
    doc( FirebaseService.firestore, `users/${ state.user.uid }` ), { idField: 'uid' } 
  ).pipe(
    takeWhile( () => !!state.user ),
    skipWhile( () => settings.appCheckFailed ),
    map( (doc: PlatformCustomer) => doc ),
    catchError( error => {
      console.error( 'Error fetching platformCustomer data:', error );
      return EMPTY;
    })
  ).subscribe( ( platformCustomer: PlatformCustomer ) => {
    console.log( 'PlatformCustomer loaded', platformCustomer );
    state.platformCustomer = platformCustomer;
    state.platformCustomerLoading = false;
    state.platformCustomerLoaded = true;
  });
}


function setBusinessCustomerListener() {
  if ( !state.user ) return;
  state.businessCustomerLoading = true;  

  const businessCustomerRef = query(
    collection( FirebaseService.firestore, `businesses/${ settings.businessId }/customers` ),
    where( 'email', '==', state.user.email ),
  );
  
  if ( state.businessCustomerSubscription ) state.businessCustomerSubscription.unsubscribe();
  return collectionSnapshots( businessCustomerRef ).pipe(
    takeWhile( () => !!state.user ),
    skipWhile( () => settings.appCheckFailed ),
    map( (snapshots: QueryDocumentSnapshot[]) => snapshots[0] ?? null ),
    skipWhile( (doc: QueryDocumentSnapshot) => !!doc?.metadata?.hasPendingWrites ),
    map( (doc: QueryDocumentSnapshot) => !doc ? null : { id: doc.id , ...doc.data() } as BusinessCustomer ),
    catchError( error => {
      console.error( 'Error fetching businessCustomer data:', error );
      return EMPTY;
    })
  ).subscribe( ( businessCustomer: BusinessCustomer ) => {
    console.log('BusinessCustomer loaded', businessCustomer);
    state.businessCustomer = businessCustomer;
    state.businessCustomerLoading = false;
    state.businessCustomerLoaded = true;
  });
}


function setCustomerOrdersListener() {
  if ( !selection.date || !state.user ) return;

  const ordersRef = query(
    collection( FirebaseService.firestore, `businesses/${ settings.businessId }/orders` ),
    where( 'customer.email', '==', state.user.email ),
    where( 'status', 'in', ['active', 'confirmed'] ),
    where( 'date', '==', selection.date )
  ).withConverter( orderConverter );
  
  try {
    state.customerOrdersLoading = true;
    if ( state.customerOrdersSubscription ) state.customerOrdersSubscription.unsubscribe();
    state.customerOrdersSubscription = collectionData( 
      ordersRef, 
      { idField: 'id' }
    ).pipe(
      takeWhile( () => !!state.user && !!selection.date ),
      skipWhile( () => settings.appCheckFailed ),
      catchError( error => {
        console.error( 'Error fetching customer orders:', error );
        return EMPTY;
      })
    ).subscribe( ( customerOrders: Order[] ) => {
      console.log( 'setCustomerOrdersListener', customerOrders );
      state.customerOrders = customerOrders;
      state.customerOrdersLoaded = true;
      state.customerOrdersLoading = false;
    });
  } catch(e) {
    console.log('loadCustomerOrders', e);
  }
}


function setCustomerListeners() {
  setUserListener();
}


function stopCustomerListeners() {
  if ( state.userSubscription ) state.userSubscription.unsubscribe();
  if ( state.platformCustomerSubscription ) state.platformCustomerSubscription.unsubscribe();
  if ( state.businessCustomerSubscription ) state.businessCustomerSubscription.unsubscribe();
  if ( state.customerOrdersSubscription ) state.customerOrdersSubscription.unsubscribe();
}


export { 
  state as customer,
  setCustomerListeners,
  stopCustomerListeners,
  setCustomerOrdersListener
};